import { array } from "../../utils/arrayUtils";
import { assemblyOperationsOfferedBy } from "../bench-work/plan-operations";
import { isBoard, palletBoard } from "../board-helpers";
import { Operation, OperationPhase } from "../Machine";
import { makeMaterial } from "../material-helpers";
import {
  Pallet,
  EndGrainSlice,
  Board,
  JIG_GRADE_KINDS,
  MaterialInstance,
  Panel,
  RACK_GRADE_KINDS,
  SHOP_FURNITURE_KINDS,
  Species,
} from "../Materials";
import { isPanel, panel } from "../panel-helpers";

// The sheet-grade vocabulary lives with the materials now (blueprints
// read it too); re-exported here for the recipes and tests that always
// imported it from the bench.
export {
  JIG_GRADE_KINDS,
  RACK_GRADE_KINDS,
  SHOP_FURNITURE_KINDS,
} from "../Materials";

/**
 * Every glue-up cures for the same long stretch once it's in the clamps —
 * the glue doesn't care how many strips you fed it. Curing is hands-free;
 * only the spread-and-clamp handwork differs per operation.
 */
export const GLUE_CURE_MINUTES = 60;

function gluePhases(clampMinutes: number): ReadonlyArray<OperationPhase> {
  return [
    { name: "Glue & Clamp", duration: clampMinutes, attended: true },
    { name: "Curing", duration: GLUE_CURE_MINUTES, attended: false },
  ];
}

/**
 * A glue-up's output: the strips of everything in the run, concatenated
 * in the order it lay — boards contribute one strip, panels all of
 * theirs. The recipes declare fixed shapes (five strips, a panel and a
 * board) as their canonical previews, but the scene's clamps-first path
 * commits whatever contiguous run was tightened, so the outputs take
 * any composition of two or more (see bench-work/glue-up.ts). Squeeze-out
 * and alignment ridges mean the panel always comes out rough.
 */
function glueUpOutput(materials: ReadonlyArray<MaterialInstance>): {
  inputs: ReadonlyArray<MaterialInstance>;
  outputs: ReadonlyArray<MaterialInstance>;
} {
  const pieces = materials.filter(
    (m): m is Board | Panel => isBoard(m) || isPanel(m),
  );
  if (pieces.length < 2) {
    throw new Error("A glue-up takes at least two pieces");
  }
  const strips = pieces.flatMap((piece) =>
    isPanel(piece)
      ? piece.strips
      : [{ species: piece.species, width: piece.width }],
  );
  return {
    inputs: [],
    outputs: [panel(strips, pieces[0].length, pieces[0].thickness, "rough")],
  };
}

/** The most common strip species in a panel (ties go to first appearance). */
function dominantSpecies(strips: ReadonlyArray<{ species: Species }>): Species {
  const counts = new Map<Species, number>();
  for (const strip of strips) {
    counts.set(strip.species, (counts.get(strip.species) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * The hand-work recipe list shared by every bench-style station: the
 * makeshift workbench you start with and the worktables you build. A
 * better bench doesn't know more recipes — it runs the attended work
 * faster (MachineType.workSpeed).
 */
export const BENCH_OPERATIONS: ReadonlyArray<Operation> = [
  {
    name: "Dismantle Pallet",
    id: "dismantlePallet",
    requiredSkill: "basicMilling",
    duration: 4,
    // Prying happens nail by nail through pryPalletNailAction — the
    // operation is never "run"; this output survives as the whole-teardown
    // shape the recipe promises (previews, and one pry-step per legacy run)
    interaction: { kind: "pry" },
    getInputMaterials: () => [{ type: ["pallet"], quantity: 1 }],
    output: (materials: ReadonlyArray<MaterialInstance>) => {
      const inputPallet = materials[0];
      if (inputPallet.type !== "pallet") {
        throw new Error("Input material is not a pallet");
      }

      const deckBoardsCount = inputPallet.deckBoards.filter(
        (board: boolean) => board,
      ).length;
      // Every pried nail goes back into the shop's stock — one per
      // deck-board × stringer crossing (Pallet.nails). A whole pallet
      // worth of prying keeps the rustic shelf free.
      if (deckBoardsCount <= 1) {
        const stringerCount = inputPallet.stringers.filter(
          (present: boolean) => present,
        ).length;
        const boardsLeft = array(stringerCount + deckBoardsCount).map(() =>
          palletBoard(),
        );
        return {
          inputs: [],
          outputs: boardsLeft,
          consumableOutputs: [
            { id: "nails" as const, amount: inputPallet.nails.length },
          ],
        };
      } else {
        const deckBoardsLeft = [
          ...inputPallet.deckBoards,
        ] as typeof inputPallet.deckBoards;
        const index = deckBoardsLeft.findLastIndex(
          (board: boolean) => board === true,
        );
        deckBoardsLeft[index] = false;
        // The board comes off with all the nails that held it; with
        // deck boards still left, every stringer keeps at least one
        const nailsLeft = inputPallet.nails.filter((n) => n.deck !== index);
        return {
          inputs: [
            makeMaterial<Pallet>({
              ...inputPallet,
              deckBoards: deckBoardsLeft,
              nails: nailsLeft,
            }),
          ],
          outputs: [palletBoard()],
          consumableOutputs: [
            {
              id: "nails" as const,
              amount: inputPallet.nails.length - nailsLeft.length,
            },
          ],
        };
      }
    },
  },
  {
    name: "Glue Up Panel",
    id: "glueUpPanel",
    requiredSkill: "panelWork",
    duration: 8 + GLUE_CURE_MINUTES,
    phases: gluePhases(8),
    interaction: { kind: "glue" },
    getInputMaterials: () => [
      {
        type: ["board"],
        width: [2],
        length: [24],
        thickness: [4],
        // Edge joints need straight edges, and clean faces make good
        // glue joints — rough stock won't do
        jointedEdges: [2],
        surface: ["smooth", "sanded"],
        quantity: 5,
      },
    ],
    // Strip order is preserved, so multi-species glue-ups keep their
    // pattern — the recipe doesn't care, but future two-tone boards do.
    output: glueUpOutput,
  },
  {
    name: "Glue Up Pair",
    id: "glueUpPair",
    requiredSkill: "freeformLamination",
    duration: 5 + GLUE_CURE_MINUTES,
    phases: gluePhases(5),
    interaction: { kind: "glue" },
    getInputMaterials: () => [
      {
        type: ["board"],
        length: [24],
        thickness: [4],
        jointedEdges: [2],
        surface: ["smooth", "sanded"],
        quantity: 2,
      },
    ],
    output: glueUpOutput,
  },
  {
    name: "Glue On Strip",
    id: "extendPanel",
    requiredSkill: "freeformLamination",
    duration: 5 + GLUE_CURE_MINUTES,
    phases: gluePhases(5),
    interaction: { kind: "glue" },
    getInputMaterials: () => [
      { type: ["panel"], length: [24], thickness: [4], quantity: 1 },
      {
        type: ["board"],
        length: [24],
        thickness: [4],
        jointedEdges: [2],
        surface: ["smooth", "sanded"],
        quantity: 1,
      },
    ],
    // Fresh squeeze-out re-roughs the whole panel, so sand last
    output: glueUpOutput,
  },
  {
    name: "Join Panels",
    id: "joinPanels",
    requiredSkill: "freeformLamination",
    duration: 8 + GLUE_CURE_MINUTES,
    phases: gluePhases(8),
    interaction: { kind: "glue" },
    getInputMaterials: () => [
      { type: ["panel"], length: [24], thickness: [4], quantity: 2 },
    ],
    // How a real shop stages a wide glue-up: sub-panels first, then one
    // joint to marry them. Strip order: the order they lay.
    output: glueUpOutput,
  },
  {
    name: "Glue Up End-Grain Panel",
    id: "glueUpEndGrain",
    requiredSkill: "endGrainBoards",
    duration: 8 + GLUE_CURE_MINUTES,
    phases: gluePhases(8),
    interaction: { kind: "glue" },
    getInputMaterials: () => [{ type: ["endGrainSlice"], quantity: 4 }],
    output: (materials: ReadonlyArray<MaterialInstance>) => {
      const slices = materials.filter(
        (m): m is EndGrainSlice => m.type === "endGrainSlice",
      );
      if (slices.length !== 4) {
        throw new Error("Need exactly 4 slices for an end-grain glue-up");
      }
      // Stood on end and clamped: a short, thick blank showing the
      // source pattern as its face. 8/4 thick — butcher-block territory.
      return {
        inputs: [],
        outputs: [
          makeMaterial<Panel>({
            type: "panel",
            grain: "end",
            strips: slices[0].strips,
            length: 12,
            thickness: 8,
            surface: "rough",
          }),
        ],
      };
    },
  },
  // The plan drawer's builds every bench offers on its own — jigs,
  // fine goods, furniture, shop furniture. Each reads whole off its
  // plan record (src/game/plans/); the hammer and drill carry the rest.
  ...assemblyOperationsOfferedBy("bench"),
];
